import { Api } from "../../services/Api";

const message = (e, fallback) => e?.response?.data?.message ?? fallback;

// Matches LoginCode::generate().
const CODE_LENGTH = 6;

// Email-code sign-in: email, then a code field under it, with password as a
// fallback for accounts that have one. request-code answers the same way
// whether or not an account exists; check-email (for the password link) doesn't.
export default (config = {}) => ({
    step: "email",
    email: "",
    code: "",
    rememberMe: false,
    busy: false,
    error: "",
    // Only accounts with a password are offered "Sign in with password".
    hasPassword: false,

    init() {
        this.$watch("step", () => {
            this.error = "";
        });
    },

    // Checkout opens sign-in with the email the customer already typed, and
    // sends the code straight away when it knows the email has an account.
    // Several copies can be on a page (nav dropdown), so only the named one acts.
    start({ target, email, send = false }) {
        if (target !== config.target) {
            return;
        }

        this.restart();
        if (email && !email.includes("orders.debrand.com")) {
            this.email = email;
        }
        if (send && this.email) {
            this.requestCode();
        }
    },

    async requestCode() {
        if (!this.email) {
            this.error = "Enter your email address.";
            return;
        }

        this.busy = true;
        this.error = "";
        try {
            await Api.post("/actions/debrand/login/request-code", {
                email: this.email,
            });
            this.hasPassword = await this.checkHasPassword();
            this.code = "";
            this.step = "code";
            this.$nextTick(() => this.$refs.code?.focus());
        } catch (e) {
            this.error = message(
                e,
                "We couldn’t send a code. Please try again.",
            );
        } finally {
            this.busy = false;
        }
    },

    async checkHasPassword() {
        try {
            const res = await Api.post("/actions/debrand/login/check-email", {
                email: this.email,
            });
            return Boolean(res.data.hasPassword);
        } catch (e) {
            return false;
        }
    },

    // Codes are 6 digits; drop anything else so a pasted "123 456" or
    // "Code: 123456" still works.
    normalizeCode() {
        this.code = this.code.replace(/\D/g, "").slice(0, CODE_LENGTH);
    },

    // Sign in once all six digits are in.
    enterCode() {
        this.normalizeCode();
        if (this.code.length === CODE_LENGTH && !this.busy) {
            this.verifyCode();
        }
    },

    async verifyCode() {
        if (this.code.length !== CODE_LENGTH) {
            this.error = "Enter the 6-digit code from the email.";
            this.$refs.code?.focus();
            return;
        }

        this.busy = true;
        this.error = "";
        try {
            // `action` in the body lets OrderService carry the guest cart over.
            const res = await Api.post("/actions/debrand/login/verify-code", {
                action: "debrand/login/verify-code",
                email: this.email,
                code: this.code,
                rememberMe: this.rememberMe ? 1 : 0,
            });
            this.signedIn(res.data.returnUrl);
        } catch (e) {
            this.busy = false;
            this.error = message(e, "That code is invalid or has expired.");
            // Start the boxes over, ready for another try.
            this.code = "";
            this.$nextTick(() => this.$refs.code?.focus());
        }
    },

    async passwordLogin() {
        const password =
            this.$root.querySelector('input[name="password"]')?.value ?? "";

        this.busy = true;
        this.error = "";
        try {
            const res = await Api.post("/actions/users/login", {
                action: "users/login",
                loginName: this.email,
                password,
                rememberMe: this.rememberMe ? 1 : 0,
            });
            this.signedIn(res.data.returnUrl);
        } catch (e) {
            this.busy = false;
            this.error = message(e, "Invalid email or password.");
        }
    },

    usePassword() {
        this.step = "password";
    },

    restart() {
        this.code = "";
        this.hasPassword = false;
        this.step = "email";
    },

    signedIn(returnUrl) {
        if (config.inline) {
            this.busy = false;
            this.$dispatch("signed-in");
            return;
        }

        window.location.href =
            config.redirectUrl || returnUrl || "/account/dashboard";
    },
});
